export const MIN_WIDTH = 1280;
export const MIN_HEIGHT = 720;

/**
 * Returns true if the given dimensions are below the minimum thresholds.
 */
export function isLowResolution(width: number, height: number): boolean {
    return width < MIN_WIDTH || height < MIN_HEIGHT;
}

export const imageOptimize = (
    url: string,
    size: string,
    format: string = 'avif',
): string => {
    // Handle proxy URLs - return them as-is without optimization
    if (url.includes('/proxy-image?url=')) {
        return url;
    }

    const cleanUrl = url.split('?')[0];

    if (!cleanUrl) {
        return '';
    }

    // Handle altai-travel.com URLs
    if (cleanUrl.startsWith('https://photos.altai-travel.com/')) {
        let optimizedUrl = cleanUrl.replace(
            'https://photos.altai-travel.com/',
            'https://media.decathlontravel.com/a/assets/',
        );

        if (format) {
            optimizedUrl = optimizedUrl.replace(/(webp|png|jpe?g)$/i, format);
        }

        return optimizedUrl.replace(/\/\d+x\d+\//, `/${size}/`);
    }

    // Handle media.decathlontravel.com URLs
    if (cleanUrl.startsWith('https://media.decathlontravel.com/assets/')) {
        let imageId = cleanUrl.replace(
            'https://media.decathlontravel.com/assets/',
            '',
        );

        if (format) {
            imageId = imageId.replace(/(webp|png|jpe?g)$/i, format);
        }

        return `https://media.decathlontravel.com/assets/${size}/${imageId}`;
    }

    // For other URLs, just change format if specified
    if (format) {
        return cleanUrl.replace(/(webp|png|jpe?g)$/i, format);
    }

    return cleanUrl;
};

export type UploadedImage = {
    url: string;
    width: number;
    height: number;
};

export async function uploadImageToMedia(
    apiEndpoint: string,
    mediaBaseUrl: string,
    file: File,
): Promise<UploadedImage> {
    const formData = new FormData();
    formData.append('content', file);
    const filenameWithoutExtension = file.name.includes('.')
        ? file.name.split('.').slice(0, -1).join('.')
        : file.name;
    formData.append('name', filenameWithoutExtension);

    const response = await fetch(apiEndpoint, {
        method: 'POST',
        body: formData,
        headers: {
            Accept: 'application/json',
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        let detail = `Upload failed for ${file.name}: ${response.statusText || response.status}`;
        try {
            const errorJson = JSON.parse(errorText);
            detail =
                errorJson.message ||
                errorJson.error ||
                errorJson.detail ||
                errorJson.title ||
                detail;
        } catch {
            // ignore non-JSON
        }
        throw new Error(detail);
    }

    const result = await response.json();
    const imagePath = result.path;
    const width = Number(result.originalWidth ?? result.width);
    const height = Number(result.originalHeight ?? result.height);

    if (!imagePath || typeof imagePath !== 'string') {
        throw new Error(
            `Invalid response from server for ${file.name}: No image path found.`,
        );
    }

    if (!Number.isFinite(width) || !Number.isFinite(height)) {
        throw new Error(
            `Invalid response from server for ${file.name}: Missing image dimensions.`,
        );
    }

    if (/^https?:\/\//.test(imagePath)) {
        return {
            url: imagePath,
            width,
            height,
        };
    }

    const baseUrl = mediaBaseUrl.endsWith('/')
        ? mediaBaseUrl.slice(0, -1)
        : mediaBaseUrl;
    const relativePath = imagePath.startsWith('/')
        ? imagePath.substring(1)
        : imagePath;

    return {
        url: `${baseUrl}/assets/${relativePath}`,
        width,
        height,
    };
}
